/**
 * Web Push subscription helper — shared between CRM and MYIHTP.
 * Uses a provided axios-compatible client (pass `api` from /lib/api.js or /my/pushApi.js).
 *
 * Usage:
 *   const push = createPushClient(api);
 *   await push.subscribe();
 *   await push.setPreference("new_lead", false);
 */

// Determine BASE based on whether the api client already has /api in baseURL
// CRM api.js: baseURL = ${API}/api → use /push
// MYIHTP pushApi.js: baseURL = ${API} → use /api/push
function getBase(api) {
  const baseURL = api.defaults?.baseURL || "";
  return baseURL.endsWith("/api") ? "/push" : "/api/push";
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function getDeviceId() {
  let id = localStorage.getItem("ihtp_push_device_id");
  if (!id) {
    const rand = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    id = `${navigator.hardwareConcurrency || 1}-${screen.width}x${screen.height}-${rand}`;
    localStorage.setItem("ihtp_push_device_id", id);
  }
  return id;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.error("SW register failed", e);
    return null;
  }
}

export function createPushClient(api, scope = "crm") {
  const BASE = getBase(api);

  async function getVapidKey() {
    const r = await api.get(`${BASE}/vapid-key`);
    return r.data.publicKey;
  }

  async function getCurrentSubscription() {
    if (!isPushSupported()) return null;
    try {
      // Add timeout to prevent hanging if no SW registered
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("SW ready timeout")), 3000)
      );
      const reg = await Promise.race([navigator.serviceWorker.ready, timeoutPromise]);
      return reg.pushManager.getSubscription();
    } catch (e) {
      console.warn("getCurrentSubscription failed:", e.message);
      return null;
    }
  }

  async function subscribe() {
    if (!isPushSupported()) throw new Error("Push notifications não suportadas neste navegador.");
    const reg = await registerSW();
    if (!reg) throw new Error("Service Worker falhou a registar.");

    const permission = Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
    if (permission !== "granted") throw new Error("Permissão de notificações negada.");

    const vapidPub = await getVapidKey();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPub),
      });
    }

    const json = sub.toJSON();
    await api.post(`${BASE}/subscribe`, {
      device_id: getDeviceId(),
      endpoint: json.endpoint,
      keys: { auth: json.keys.auth, p256dh: json.keys.p256dh },
      scope,
    });
    return true;
  }

  async function unsubscribe() {
    const sub = await getCurrentSubscription();
    if (sub) {
      try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
    }
    try {
      await api.post(`${BASE}/unsubscribe`, { device_id: getDeviceId() });
    } catch (_) { /* ignore */ }
    return true;
  }

  async function getPreferences() {
    const r = await api.get(`${BASE}/preferences`);
    return r.data; // { preferences: {...}, devices: [...] }
  }

  async function setPreference(event_type, enabled) {
    await api.post(`${BASE}/preferences`, { event_type, enabled });
  }

  async function sendTest() {
    const r = await api.post(`${BASE}/test`);
    return r.data;
  }

  async function isSubscribedOnThisDevice() {
    const sub = await getCurrentSubscription();
    return !!sub;
  }

  return {
    isPushSupported,
    subscribe,
    unsubscribe,
    getPreferences,
    setPreference,
    sendTest,
    isSubscribedOnThisDevice,
    getDeviceId,
    registerSW,
  };
}
